import React, { useState, useEffect, useCallback } from 'react';
import { vehicleService } from '../../api/services/vehicleService';
import { Loader2, ChevronRight } from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────────────────

type ProductType = 'seat-covers' | 'cargo-liner' | 'dash-cover';

interface DropdownOption { Id: number; Name: string; }

interface AppFinderQA { QuestionId: number; Question: string; Answer: string; }

interface AppFinderPart {
  PartId: number; PartNumber: string; SeatType: string;
  SeatRowCode: string; Image: string; Extras: string;
  QuestionAnswers: AppFinderQA[];
}

interface AppFinderData {
  vehicleDesc: string;
  FrontParts: AppFinderPart[]; MiddleParts: AppFinderPart[];
  RearParts: AppFinderPart[];  CargoParts: AppFinderPart[];
  DashParts?: AppFinderPart[];
}

interface QuestionRow {
  questionId: number; question: string;
  answers: Record<string, string>; highlight: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildQuestionTable(parts: AppFinderPart[]): QuestionRow[] {
  const questionOrder: number[] = [];
  const questionTexts: Record<number, string> = {};
  for (const part of parts) {
    for (const qa of part.QuestionAnswers) {
      if (!(qa.QuestionId in questionTexts)) {
        questionOrder.push(qa.QuestionId);
        questionTexts[qa.QuestionId] = qa.Question;
      }
    }
  }
  const rows = questionOrder.map(qId => {
    const answers: Record<string, string> = {};
    for (const part of parts) {
      const qa = part.QuestionAnswers.find(q => q.QuestionId === qId);
      answers[part.PartNumber] = qa?.Answer ?? '';
    }
    const uniqueValues = new Set(Object.values(answers).filter(Boolean));
    return { questionId: qId, question: questionTexts[qId], answers, highlight: uniqueValues.size > 1 };
  });
  return [...rows.filter(r => r.highlight), ...rows.filter(r => !r.highlight)];
}

// ── Section Table ─────────────────────────────────────────────────────────────

const SectionTable: React.FC<{ title: string; parts: AppFinderPart[]; imageBasePath: string }> = ({ title, parts, imageBasePath }) => {
  const rows = buildQuestionTable(parts);
  if (parts.length === 0) return null;

  return (
    <div className="mb-8">
      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 px-1">{title}</h3>
      <div className="overflow-x-auto rounded border border-gray-200">
        <table className="w-auto mx-auto text-sm border-collapse">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="p-3 text-left font-medium text-gray-500 border-r border-gray-200 min-w-[180px] align-bottom text-xs uppercase tracking-wide">
                Question
              </th>
              {parts.map(part => (
                <th key={part.PartNumber} className="p-3 text-center border-r border-gray-200 last:border-r-0 min-w-[200px] bg-gray-50">
                  <div className="font-bold text-gray-900 text-base">{part.PartNumber}</div>
                  {part.SeatType && <div className="text-xs text-gray-500 mt-0.5">{part.SeatType}</div>}
                  {part.Extras && <div className="text-xs text-gray-400 mt-0.5 italic">{part.Extras}</div>}
                  {part.Image && (
                    <div className="mt-2 flex justify-center">
                      <img
                        src={`${imageBasePath}/${part.Image}.jpg`}
                        alt={part.Image}
                        className="max-w-[190px] max-h-[140px] object-contain"
                        onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                      />
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={parts.length + 1} className="p-3 text-center text-gray-400 italic text-sm">
                  No specifications available
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr
                  key={row.questionId}
                  className={[
                    'border-t border-gray-200',
                    row.highlight
                      ? 'bg-blue-50'
                      : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/60',
                  ].join(' ')}
                >
                  <td className="p-3 text-gray-700 font-medium border-r border-gray-200 text-sm">
                    {row.question}
                    {row.highlight && (
                      <span className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-blue-400 align-middle" />
                    )}
                  </td>
                  {parts.map(part => (
                    <td key={part.PartNumber} className="p-3 text-center text-gray-700 border-r border-gray-200 last:border-r-0 text-sm">
                      {row.answers[part.PartNumber] || '—'}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Select helper ─────────────────────────────────────────────────────────────

const Sel: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = (props) => (
  <select
    {...props}
    className={`px-3 py-2 text-sm border border-gray-300 rounded bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:bg-gray-100 ${props.className ?? ''}`}
  />
);

// ── Main Component ────────────────────────────────────────────────────────────

const PublicAppFinder: React.FC = () => {
  const [productType,       setProductType]       = useState<ProductType>('seat-covers');
  const [selectedYear,      setSelectedYear]      = useState<number | null>(null);
  const [selectedMakeId,    setSelectedMakeId]    = useState<number | null>(null);
  const [selectedModelId,   setSelectedModelId]   = useState<number | null>(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState<number | null>(null);

  const [years,          setYears]          = useState<number[]>([]);
  const [makes,          setMakes]          = useState<DropdownOption[]>([]);
  const [models,         setModels]         = useState<DropdownOption[]>([]);
  const [vehicleOptions, setVehicleOptions] = useState<DropdownOption[]>([]);
  const [partsData,      setPartsData]      = useState<AppFinderData | null>(null);

  const [loadingYears,   setLoadingYears]   = useState(false);
  const [loadingMakes,   setLoadingMakes]   = useState(false);
  const [loadingModels,  setLoadingModels]  = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [loadingParts,   setLoadingParts]   = useState(false);
  const [error,          setError]          = useState('');

  const isCargo = productType === 'cargo-liner';
  const isDash = productType === 'dash-cover';
  const anyLoading = loadingYears || loadingMakes || loadingModels || loadingOptions || loadingParts;

  const resetAll = useCallback(() => {
    setSelectedYear(null); setSelectedMakeId(null); setSelectedModelId(null);
    setSelectedVehicleId(null); setYears([]); setMakes([]); setModels([]);
    setVehicleOptions([]); setPartsData(null); setError('');
  }, []);

  const handleProductTypeChange = useCallback((type: ProductType) => {
    setProductType(type);
    resetAll();
  }, [resetAll]);

  useEffect(() => {
    setLoadingYears(true);
    const fetch = isCargo
      ? vehicleService.getCargoAppFinderYears()
      : isDash
      ? vehicleService.getDashAppFinderYears()
      : vehicleService.getAppFinderYears();
    fetch
      .then(setYears)
      .catch(() => setError('Failed to load years'))
      .finally(() => setLoadingYears(false));
  }, [isCargo, isDash]);

  const handleYearChange = useCallback(async (raw: string) => {
    const year = raw ? parseInt(raw) : null;
    setSelectedYear(year); setSelectedMakeId(null); setSelectedModelId(null);
    setSelectedVehicleId(null); setMakes([]); setModels([]); setVehicleOptions([]); setPartsData(null);
    if (!year) return;
    setLoadingMakes(true);
    try {
      const data = isCargo
        ? await vehicleService.getCargoAppFinderMakes(year)
        : isDash
        ? await vehicleService.getDashAppFinderMakes(year)
        : await vehicleService.getAppFinderMakes(year);
      setMakes(data);
    } catch { setError('Failed to load makes'); }
    finally { setLoadingMakes(false); }
  }, [isCargo, isDash]);

  const handleMakeChange = useCallback(async (raw: string) => {
    const makeId = raw ? parseInt(raw) : null;
    setSelectedMakeId(makeId); setSelectedModelId(null); setSelectedVehicleId(null);
    setModels([]); setVehicleOptions([]); setPartsData(null);
    if (!makeId || !selectedYear) return;
    setLoadingModels(true);
    try {
      const data = isCargo
        ? await vehicleService.getCargoAppFinderModels(selectedYear, makeId)
        : isDash
        ? await vehicleService.getDashAppFinderModels(selectedYear, makeId)
        : await vehicleService.getAppFinderModels(selectedYear, makeId);
      setModels(data);
    } catch { setError('Failed to load models'); }
    finally { setLoadingModels(false); }
  }, [selectedYear, isCargo, isDash]);

  const handleVehicleSelect = useCallback(async (vehicleId: number) => {
    setSelectedVehicleId(vehicleId); setPartsData(null);
    setLoadingParts(true);
    try {
      const data = isCargo
        ? await vehicleService.getCargoAppFinderPartsData(vehicleId)
        : isDash
        ? await vehicleService.getDashAppFinderPartsData(vehicleId)
        : await vehicleService.getAppFinderPartsData(vehicleId);
      setPartsData(data);
    } catch { setError('Failed to load part data'); }
    finally { setLoadingParts(false); }
  }, [isCargo, isDash]);

  const handleModelChange = useCallback(async (raw: string) => {
    const modelId = raw ? parseInt(raw) : null;
    setSelectedModelId(modelId); setSelectedVehicleId(null); setVehicleOptions([]); setPartsData(null);
    if (!modelId || !selectedYear || !selectedMakeId) return;
    setLoadingOptions(true);
    try {
      const data = isCargo
        ? await vehicleService.getCargoAppFinderVehicleOptions(selectedYear, selectedMakeId, modelId)
        : isDash
        ? await vehicleService.getDashAppFinderVehicleOptions(selectedYear, selectedMakeId, modelId)
        : await vehicleService.getAppFinderVehicleOptions(selectedYear, selectedMakeId, modelId);
      setVehicleOptions(data);
      if (data.length === 1) handleVehicleSelect(data[0].Id);
    } catch { setError('Failed to load vehicle options'); }
    finally { setLoadingOptions(false); }
  }, [selectedYear, selectedMakeId, handleVehicleSelect, isCargo, isDash]);

  const sections = partsData
    ? isCargo
      ? [{ title: 'Cargo Liner Parts', parts: partsData.CargoParts ?? [] }].filter(s => s.parts.length > 0)
      : isDash
      ? [{ title: 'Dash Cover Parts', parts: partsData.DashParts ?? [] }].filter(s => s.parts.length > 0)
      : [
          { title: 'Front Styles',  parts: partsData.FrontParts  ?? [] },
          { title: 'Middle Styles', parts: partsData.MiddleParts ?? [] },
          { title: 'Rear Styles',   parts: partsData.RearParts   ?? [] },
          { title: 'Cargo Items',   parts: partsData.CargoParts  ?? [] },
        ].filter(s => s.parts.length > 0)
    : [];

  return (
    <div className="min-h-screen bg-gray-100 font-sans">

      {/* Top bar */}
      <div className="bg-white border-b border-gray-300 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="text-base font-bold text-gray-800">CalTrend App Finder</span>
            {anyLoading && <Loader2 size={16} className="animate-spin text-blue-500" />}
          </div>
          {/* Product type toggle */}
          <div className="flex rounded border border-gray-300 overflow-hidden text-sm font-medium">
            <button
              onClick={() => handleProductTypeChange('seat-covers')}
              className={[
                'px-4 py-1.5 transition-colors',
                productType === 'seat-covers'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50',
              ].join(' ')}
            >
              Seat Covers
            </button>
            <button
              onClick={() => handleProductTypeChange('cargo-liner')}
              className={[
                'px-4 py-1.5 border-l border-gray-300 transition-colors',
                productType === 'cargo-liner'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50',
              ].join(' ')}
            >
              Cargo Liner
            </button>
            <button
              onClick={() => handleProductTypeChange('dash-cover')}
              className={[
                'px-4 py-1.5 border-l border-gray-300 transition-colors',
                productType === 'dash-cover'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50',
              ].join(' ')}
            >
              Dash Cover
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">

        {/* Selector card */}
        <div className="bg-white rounded border border-gray-300 shadow-sm mb-6">
          <div className="px-5 py-4">
            <div className="flex flex-wrap items-end gap-4">

              {/* Year */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Year</label>
                <Sel value={selectedYear ?? ''} onChange={e => handleYearChange(e.target.value)} disabled={loadingYears} className="w-28">
                  <option value="">[Year]</option>
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
                </Sel>
              </div>

              {selectedYear && (
                <>
                  <ChevronRight size={14} className="text-gray-400 mb-2 hidden sm:block" />
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Make</label>
                    <Sel value={selectedMakeId ?? ''} onChange={e => handleMakeChange(e.target.value)} disabled={loadingMakes} className="w-40">
                      <option value="">{loadingMakes ? 'Loading…' : '[Select Make]'}</option>
                      {makes.map(m => <option key={m.Id} value={m.Id}>{m.Name}</option>)}
                    </Sel>
                  </div>
                </>
              )}

              {selectedMakeId && (
                <>
                  <ChevronRight size={14} className="text-gray-400 mb-2 hidden sm:block" />
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Model</label>
                    <Sel value={selectedModelId ?? ''} onChange={e => handleModelChange(e.target.value)} disabled={loadingModels} className="w-44">
                      <option value="">{loadingModels ? 'Loading…' : '[Select Model]'}</option>
                      {models.map(m => <option key={m.Id} value={m.Id}>{m.Name}</option>)}
                    </Sel>
                  </div>
                </>
              )}

              {selectedModelId && vehicleOptions.length > 1 && (
                <>
                  <ChevronRight size={14} className="text-gray-400 mb-2 hidden sm:block" />
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Style</label>
                    <Sel value={selectedVehicleId ?? ''} onChange={e => e.target.value && handleVehicleSelect(parseInt(e.target.value))} disabled={loadingOptions} className="w-56">
                      <option value="">{loadingOptions ? 'Loading…' : '[Select Style]'}</option>
                      {vehicleOptions.map(v => <option key={v.Id} value={v.Id}>{v.Name}</option>)}
                    </Sel>
                  </div>
                </>
              )}
            </div>

            {error && <p className="mt-3 text-sm text-red-500">{error}</p>}
          </div>
        </div>

        {/* Results */}
        {loadingParts && (
          <div className="flex justify-center py-16">
            <Loader2 size={28} className="animate-spin text-blue-500" />
          </div>
        )}

        {partsData && !loadingParts && (
          <>
            <h2 className="text-base font-semibold text-gray-800 mb-5">{partsData.vehicleDesc}</h2>
            {sections.length === 0 ? (
              <div className="bg-white rounded border border-gray-200 p-8 text-center text-gray-400 text-sm">
                No applications found for this vehicle.
              </div>
            ) : (
              sections.map(s => (
                <SectionTable key={s.title} title={s.title} parts={s.parts} imageBasePath={isDash ? '/dash-diagrams' : '/seat-diagrams'} />
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PublicAppFinder;
